import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";

const getAcademicSession = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0 = January, 3 = April

  // If month is April (3) or later, session is currentYear/nextYear
  // If before April, session is previousYear/currentYear
  if (month >= 3) {
    return `${year}/${year + 1}`;
  } else {
    return `${year - 1}/${year}`;
  }
};

const Hero = () => {
  const academicSession = getAcademicSession();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section
      className="relative min-h-screen flex items-center"
      style={{ zIndex: 10 }}
    >
      {/* Background Image with Ken Burns Effect */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 animate-kenburns">
          <img
            src="https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=1920&q=80"
            alt="Students learning"
            className="w-full h-full object-cover"
          />
        </div>
        {/* Gradient Overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to right, rgba(12, 26, 61, 0.95), rgba(30, 58, 138, 0.85), rgba(30, 58, 138, 0.5))",
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 pt-32">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-3xl"
        >
          {/* Badge */}
          <motion.div variants={itemVariants} className="mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-medium">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              Now Accepting Applications for {academicSession}
            </span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            variants={itemVariants}
            className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-6"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              color: "white",
            }}
          >
            Where Every Child's{" "}
            <span style={{ color: "#facc15" }}>Potential Unfolds</span>
          </motion.h1>

          {/* Subheading */}
          <motion.p
            variants={itemVariants}
            className="text-lg md:text-xl mb-8 max-w-2xl leading-relaxed"
            style={{ color: "rgba(255, 255, 255, 0.9)" }}
          >
            At Stirret Schools, we nurture curious minds and bold thinkers from
            early childhood through secondary education, preparing students for
            global success.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div variants={itemVariants} className="flex flex-wrap gap-4">
            <Link to="/admissions" className="btn-primary group">
              Start Your Journey
              <ArrowRight
                className="ml-2 group-hover:translate-x-1 transition-transform"
                size={20}
              />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white border-2 border-white/30 rounded-lg hover:bg-white/10 backdrop-blur-sm transition-all duration-200"
            >
              <Play className="mr-2" size={20} />
              Discover Our Story
            </Link>
          </motion.div>

          {/* Quick Stats */}
          {/* <motion.div
            variants={itemVariants}
            className="mt-12 pt-8 grid grid-cols-2 md:grid-cols-4 gap-6"
            style={{ borderTop: "1px solid rgba(255, 255, 255, 0.2)" }}
          >
            {[
              { value: "1000+", label: "Students" },
              { value: "95%", label: "University Rate" },
              { value: "9+", label: "Years" },
              { value: "50+", label: "Educators" },
            ].map((stat, index) => (
              <div key={index} className="text-center md:text-left">
                <div
                  className="text-2xl md:text-3xl font-bold"
                  style={{ color: "white" }}
                >
                  {stat.value}
                </div>
                <div
                  className="text-sm"
                  style={{ color: "rgba(255, 255, 255, 0.7)" }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div> */}
        </motion.div>
      </div>

      {/* School Division Cards */}
      <div
        className="absolute bottom-0 left-0 right-0 hidden lg:block"
        style={{ zIndex: 30 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="grid grid-cols-2 gap-4"
            style={{ transform: "translateY(50%)" }}
          >
            <Link
              to="/montessori"
              className="group block p-6 bg-emerald-700 hover:bg-emerald-600 rounded-t-2xl text-white transition-all duration-300 hover:-translate-y-2"
            >
              <span className="text-sm text-emerald-200 font-medium">
                Ages 2-11
              </span>
              <h3 className="text-xl font-bold mt-1 mb-2">
                Montessori & Primary
              </h3>
              <p className="text-emerald-100 text-sm">
                Child-centered learning in a nurturing environment
              </p>
              <div className="mt-4 flex items-center text-emerald-200 font-medium group-hover:text-white transition-colors">
                Explore
                <ArrowRight
                  className="ml-2 group-hover:translate-x-1 transition-transform"
                  size={18}
                />
              </div>
            </Link>
            <Link
              to="/college"
              className="group block p-6 bg-primary-700 hover:bg-primary-600 rounded-t-2xl text-white transition-all duration-300 hover:-translate-y-2"
            >
              <span className="text-sm text-primary-200 font-medium">
                Ages 11-18
              </span>
              <h3 className="text-xl font-bold mt-1 mb-2">Stirret College</h3>
              <p className="text-primary-100 text-sm">
                Academic excellence meets holistic development
              </p>
              <div className="mt-4 flex items-center text-primary-200 font-medium group-hover:text-white transition-colors">
                Explore
                <ArrowRight
                  className="ml-2 group-hover:translate-x-1 transition-transform"
                  size={18}
                />
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile School Cards */}
      <div className="lg:hidden absolute bottom-0 left-0 right-0 px-4 pb-4" style={{ zIndex: 20 }}>
        <div className="grid grid-cols-2 gap-3">
          <Link
            to="/montessori"
            className="block p-4 bg-emerald-700 rounded-xl text-white"
          >
            <span className="text-xs text-emerald-200">Ages 2-11</span>
            <h3 className="text-sm font-bold mt-1">Montessori & Primary</h3>
          </Link>
          <Link
            to="/college"
            className="block p-4 bg-primary-700 rounded-xl text-white"
          >
            <span className="text-xs text-primary-200">Ages 11-18</span>
            <h3 className="text-sm font-bold mt-1">Stirret College</h3>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Hero;
